export interface AuftragsDaten {
  kategorie: string | null;
  objekt: { strasse: string | null; hausnummer: string | null; plz: string | null; ort: string | null; objektnummer: string | null; bezeichnung: string | null } | null;
  einheit: string | null;
  eigentuemer: string | null;
  mangelBeschreibung: string | null;
  meldender: { name: string | null; rolle: string; telefon: string | null; email: string | null } | null;
  /** id: beauftragter Dienstleister (tickets.dienstleister_id) – fehlt bei frei eingegebenem Empfänger */
  dienstleister: { id?: string | null; name: string | null; email: string | null; telefon: string | null } | null;
}
export interface AuftragsMail { empfaenger: string; betreff: string; text: string; }

const clean = (v: string | null | undefined) => (v && v.trim() ? v.trim() : '');
const mark = (v: string, feld: string) => (v ? v : `[bitte ergänzen: ${feld}]`);

function objektKurz(o: AuftragsDaten['objekt']): string {
  return [clean(o?.strasse), clean(o?.hausnummer)].filter(Boolean).join(' ');
}

/**
 * #525: Objekt für den Betreff – „Treiser Straße 18+20, Staufenberg“ (+ „ – Einheit“, wenn bekannt).
 * Leer, wenn kein Objekt bekannt ist.
 */
export function auftragsBetreffObjekt(d: Pick<AuftragsDaten, 'objekt' | 'einheit'>): string {
  const objekt = [objektKurz(d.objekt), clean(d.objekt?.ort)].filter(Boolean).join(', ');
  return [objekt, clean(d.einheit)].filter(Boolean).join(' – ');
}
function objektAdresse(o: AuftragsDaten['objekt']): string {
  const zeile1 = [clean(o?.strasse), clean(o?.hausnummer)].filter(Boolean).join(' ');
  const zeile2 = [clean(o?.plz), clean(o?.ort)].filter(Boolean).join(' ');
  return [zeile1, zeile2].filter(Boolean).join(', ');
}

/**
 * Platzhalter-Werte für die E-Mail-Vorlage „Handwerkerbeauftragung" (ki_trigger
 * handwerker_beauftragung) aus den Auftragsdaten. Die Vorlage nutzt historisch
 * {{mieter.*}} für die Kontaktdaten des MELDENDEN — deshalb das Mapping; zusätzlich
 * werden sauber benannte {{meldender.*}}/{{auftrag.*}}-Platzhalter geliefert, damit
 * eine überarbeitete Vorlage direkt darauf umsteigen kann.
 * Fehlende Werte bleiben LEER (User-Entscheidung 2026-07-23) — keine
 * „[bitte ergänzen: …]"-Marker in der Mail; Lücken werden im Vorbereiten-Dialog
 * oder im Briefbogen selbst ergänzt.
 */
export function auftragsPlatzhalterWerte(d: AuftragsDaten): Record<string, string> {
  const name = clean(d.meldender?.name);
  const telefon = clean(d.meldender?.telefon);
  const email = clean(d.meldender?.email);
  return {
    'objekt.strasse': clean(d.objekt?.strasse),
    'objekt.hausnummer': clean(d.objekt?.hausnummer),
    'objekt.plz': clean(d.objekt?.plz),
    'objekt.ort': clean(d.objekt?.ort),
    'objekt.bezeichnung': clean(d.objekt?.bezeichnung),
    // Vorlage adressiert den Meldenden über mieter.*-Platzhalter
    'mieter.anrede': '',
    'mieter.vorname': '',
    'mieter.nachname': name,
    'mieter.telefon': telefon,
    'mieter.email': email,
    // Sauber benannte Varianten für die künftige, überarbeitete Vorlage
    'meldender.name': name,
    'meldender.rolle': clean(d.meldender?.rolle),
    'meldender.telefon': telefon,
    'meldender.email': email,
    'auftrag.einheit': clean(d.einheit),
    // #525: Objekt für Betreff/Text der Vorlage („Treiser Straße 18+20, Staufenberg – Whg. 3“)
    'auftrag.objekt': auftragsBetreffObjekt(d),
    'auftrag.eigentuemer': clean(d.eigentuemer),
    'auftrag.kategorie': clean(d.kategorie),
    'auftrag.beschreibung': clean(d.mangelBeschreibung),
  };
}

/**
 * 'auftrag' = verbindliche Beauftragung, 'angebot' = unverbindliche Angebotsanfrage.
 * Beide teilen Objektdaten, Mangel und Kontakt — sie unterscheiden sich in Betreff,
 * Einleitung und Schluss (Angebot: kein Ausführungsauftrag, keine Rechnungsanschrift).
 */
export type AuftragsArt = 'auftrag' | 'angebot';

export function baueAuftragsMail(d: AuftragsDaten, art: AuftragsArt = 'auftrag'): AuftragsMail {
  const kategorie = mark(clean(d.kategorie), 'Kategorie');
  const einheit = mark(clean(d.einheit), 'Einheit');
  const empfaenger = clean(d.dienstleister?.email);
  const angebot = art === 'angebot';

  // Betreff: fehlende Einheit nicht als Marker – sie gehört nicht in jede Anfrage (#525)
  const betreff = `${angebot ? 'Angebotsanfrage' : 'Auftrag'} – ${kategorie} – ${auftragsBetreffObjekt(d) || mark('', 'Objekt')}`;

  const eigentuemer = mark(clean(d.eigentuemer), 'Eigentümer / WEG');
  if (angebot) {
    return {
      empfaenger,
      betreff,
      text: [
        'Sehr geehrte Damen und Herren,',
        '',
        'bevor wir Sie mit der Behebung des nachfolgend beschriebenen Mangels beauftragen,',
        'bitten wir Sie um ein unverbindliches Angebot.',
        '',
        'Objektdaten',
        `Objekt: ${mark(objektAdresse(d.objekt), 'Objektadresse')}`,
        `Wohnung/Einheit: ${einheit}`,
        `Eigentümer / WEG: ${eigentuemer}`,
        '',
        'Wofür wir ein Angebot benötigen',
        `Kategorie: ${kategorie}`,
        mark(clean(d.mangelBeschreibung), 'Beschreibung der gewünschten Leistung'),
        '',
        'Kontaktdaten des Meldenden',
        `Name: ${mark(clean(d.meldender?.name), 'Name')}`,
        `Rolle: ${mark(clean(d.meldender?.rolle), 'Rolle')}`,
        `Telefon: ${mark(clean(d.meldender?.telefon), 'Telefon')}`,
        `E-Mail: ${mark(clean(d.meldender?.email), 'E-Mail')}`,
        '',
        'Für eine Besichtigung vor Ort setzen Sie sich bitte direkt mit dem oben genannten Ansprechpartner in Verbindung.',
        '',
        'Bitte senden Sie uns Ihr Angebot per E-Mail an info@immo-schwede.de. Eine Beauftragung erfolgt erst nach Freigabe des Angebots.',
        '',
        'Vielen Dank für Ihre Unterstützung.',
        '',
        'Mit freundlichen Grüßen',
      ].join('\n'),
    };
  }

  const text = [
    'Sehr geehrte Damen und Herren,',
    '',
    'wir bitten Sie, den nachfolgend beschriebenen Mangel zeitnah zu überprüfen und nach Möglichkeit zu beheben.',
    '',
    'Objektdaten',
    `Objekt: ${mark(objektAdresse(d.objekt), 'Objektadresse')}`,
    `Wohnung/Einheit: ${einheit}`,
    `Eigentümer / WEG: ${eigentuemer}`,
    '',
    'Gemeldeter Mangel',
    `Kategorie: ${kategorie}`,
    'Beschreibung:',
    mark(clean(d.mangelBeschreibung), 'Mangelbeschreibung'),
    '',
    'Kontaktdaten des Meldenden',
    `Name: ${mark(clean(d.meldender?.name), 'Name')}`,
    `Rolle: ${mark(clean(d.meldender?.rolle), 'Rolle')}`,
    `Telefon: ${mark(clean(d.meldender?.telefon), 'Telefon')}`,
    `E-Mail: ${mark(clean(d.meldender?.email), 'E-Mail')}`,
    '',
    'Bitte setzen Sie sich zur Terminvereinbarung direkt mit dem oben genannten Ansprechpartner in Verbindung.',
    '',
    'Sollte der Mangel nicht im Rahmen dieses Auftrags behoben werden können oder weitere Arbeiten erforderlich sein, bitten wir Sie, uns vorab einen entsprechenden Kostenvoranschlag zukommen zu lassen.',
    '',
    'Nach Abschluss der Arbeiten bitten wir um eine kurze Rückmeldung über die durchgeführten Arbeiten sowie – sofern sinnvoll – um aussagekräftige Fotos des behobenen Mangels.',
    '',
    'Rechnungsanschrift (bitte zwingend beachten)',
    '',
    'Bitte stellen Sie die Rechnung ausschließlich auf folgende Rechnungsanschrift aus:',
    '',
    eigentuemer,
    'c/o Immobilienmanagement Schwede',
    'Marburger Ring 32a',
    '35274 Kirchhain',
    '',
    'Bitte verwenden Sie ausschließlich diese Rechnungsanschrift. Falsch adressierte Rechnungen müssen gegebenenfalls korrigiert und neu ausgestellt werden.',
    '',
    'Bitte senden Sie die Rechnung sowie Ihre Rückmeldung nach Abschluss der Arbeiten per E-Mail an info@immo-schwede.de.',
    '',
    'Vielen Dank für Ihre Unterstützung.',
    '',
    'Mit freundlichen Grüßen',
  ].join('\n');

  return { empfaenger, betreff, text };
}
